import { FileCardRemote } from "@/components/files";
import { ArticleGroupSelect, ArticleProductTypeSelect } from "@/components/stock";
import { client } from "@/hono";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { Divider } from "@msuite/ui/divider";
import { type FileUploadController, ImageUpload } from "@msuite/ui/file-upload";
import {
	FormSwitch,
	NumberInput,
	SizeInput,
	Textarea,
	TextInput,
	WeightInput,
} from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { ArticleFormSchema } from "@msuite/utils/schemas";
import { Fragment, useEffect, useEffectEvent } from "react";
import { useForm, useFormContext, useWatch } from "react-hook-form";

/** Props Interface */
interface ArticleFormProps {
	imageUploadController: FileUploadController;
}

export function ArticleForm({ imageUploadController }: ArticleFormProps) {
	/** Context */
	const methods = useFormContext<ArticleFormSchema>();
	const innerMethods = useForm();
	const [articleGroupId] = useWatch({ control: innerMethods.control, name: ["articleGroupId"] });
	const [articleProductTypeId, autoGenerateNumber] = useWatch({
		control: methods.control,
		name: ["articleProductTypeId", "autoGenerateNumber"],
	});
	const mentionOptions = useMentionOptions();

	/** Hooks */
	const { t } = useTranslation();

	/** Effect Event */
	const getArticleGroup = useEffectEvent(async () => {
		if (articleGroupId) return;
		if (!articleProductTypeId) return;
		const response = await client.stock["article-product-types"][":articleProductTypeId"].$get({
			param: { articleProductTypeId },
		});
		const data = await responseHandler(response);
		if (!data.articleProductType?.articleGroupId) {
			toast.success("No article group found for the selected article product type.");
		} else {
			innerMethods.setValue("articleGroupId", data.articleProductType.articleGroupId);
		}
	});

	/** Effects */
	useEffect(() => {
		getArticleGroup();
	}, [articleGroupId, articleProductTypeId]);

	/** Functions */
	async function handleOnRemoveImage() {
		const fileId = imageUploadController.defaultFiles[0]?.id;
		if (!fileId) return;
		const response = await client.files[":fileId"].$delete({ param: { fileId } });
		await responseHandler(response);
	}

	/** Render */
	return (
		<div className="v-stack gap-4">
			<ArticleGroupSelect
				control={innerMethods.control}
				name="articleGroupId"
			/>
			<ArticleProductTypeSelect
				key={articleGroupId}
				control={methods.control}
				articleGroupId={articleGroupId}
				name="articleProductTypeId"
			/>
			<Divider />
			<FormSwitch
				control={methods.control}
				name="autoGenerateNumber"
			>
				{t("stock.auto_generate_number")}
			</FormSwitch>
			{!autoGenerateNumber ? (
				<Fragment>
					<NumberInput
						label={t("stock.order_no")}
						isRequired
						control={methods.control}
						name="number"
						placeholder="1"
						variant="integer"
						min={0}
						max={999}
						step={1}
					/>
					<Divider />
				</Fragment>
			) : null}
			<TextInput
				isRequired
				label={t("general.name")}
				control={methods.control}
				name="name"
				placeholder={t("placeholders.stock.article_name")}
				data-cy="article-name-input"
			/>
			<Textarea
				disableFormatting
				label={t("general.description")}
				isRequired={false}
				control={methods.control}
				name="description"
				{...mentionOptions}
			/>
			<SizeInput />
			<WeightInput />
			<NumberInput
				isRequired
				label={t("stock.warning_stock_balance")}
				control={methods.control}
				name="warningStockBalance"
				min={0}
				defaultValue={0}
			/>
			<NumberInput
				isRequired
				label={t("stock.minimum_stock_balance")}
				control={methods.control}
				name="minimumStockBalance"
				min={0}
				defaultValue={0}
			/>
			<Divider />
			<FormSwitch
				control={methods.control}
				name="requiresTransferCarrierOnReturn"
			>
				{t("stock.requires_transfer_carrier_on_return")}
			</FormSwitch>
			<Divider />
			<ImageUpload
				type="image"
				controller={imageUploadController}
				onRemove={handleOnRemoveImage}
				renderFile={(file) => (
					<FileCardRemote
						fileId={file.id}
						onInteraction={async () => {}}
					/>
				)}
			/>
		</div>
	);
}
